import { useEffect } from 'react';

import { useAuthStore } from '../../stores/authStore';
import { useConsentStore, type ConsentSnapshot } from '../../stores/consentStore';
import { userApi, type ConsentPreferences } from '../../services/userApi';

/**
 * Renders nothing. On mount (and whenever auth state flips to signed-in)
 * it reconciles the server-stored consent record (§10.7) with the local
 * one persisted by `useConsentStore`.
 *
 * Resolution rule:
 *   - Compare server `consent.updatedAt` (or `consentedAt`) to local
 *     `lastChangedAt`. Newer wins.
 *   - If server wins, write its snapshot into the store + localStorage.
 *   - If local wins, PATCH the server with the local flags. (This
 *     covers the case where a signed-out user accepted, then signed in.)
 *   - If neither side has a timestamp, do nothing (no decision yet).
 *
 * Errors are intentionally swallowed (warn-only) — the banner will
 * still drive the user through a fresh decision if state is unclear.
 */
function timestamp(value: string | null | undefined): number {
  if (!value) return 0;
  const parsed = Date.parse(value);
  return Number.isFinite(parsed) ? parsed : 0;
}

function snapshotFromServer(consent: ConsentPreferences): ConsentSnapshot {
  return {
    analytics: Boolean(consent.analytics),
    personalization: Boolean(consent.personalization),
    behavioralAdvertising: Boolean(consent.behavioralAdvertising),
    advertiserDataSharing: Boolean(consent.advertiserDataSharing),
    marketingEmail: Boolean(consent.marketingEmail),
    hasDecided: Boolean(consent.consentedAt || consent.updatedAt),
    lastChangedAt: consent.updatedAt ?? consent.consentedAt ?? null,
  };
}

async function reconcile(): Promise<void> {
  const isAuthed = useAuthStore.getState().isAuthenticated;
  if (!isAuthed) return;

  let server: ConsentPreferences;
  try {
    const result = await userApi.getPrivacy();
    server = result.consent;
  } catch (err) {
    console.warn(
      '[consent] could not fetch server consent; proceeding with local state:',
      err instanceof Error ? err.message : err
    );
    return;
  }

  const local = useConsentStore.getState();
  const localTs = timestamp(local.lastChangedAt);
  const serverTs = timestamp(server.updatedAt ?? server.consentedAt ?? null);

  if (!local.hasDecided && serverTs === 0) {
    // No decision anywhere; nothing to do.
    return;
  }

  if (serverTs > localTs) {
    useConsentStore.getState().applyServerSnapshot(snapshotFromServer(server));
    return;
  }

  // Local wins (or tie, but local is decided) — push to server.
  if (!local.hasDecided) return;
  try {
    await userApi.patchPrivacy({
      analytics: local.analytics,
      personalization: local.personalization,
      behavioralAdvertising: local.behavioralAdvertising,
      advertiserDataSharing: local.advertiserDataSharing,
      marketingEmail: local.marketingEmail,
    });
  } catch (err) {
    console.warn(
      '[consent] could not push local consent to server:',
      err instanceof Error ? err.message : err
    );
  }
}

export function ConsentManagedSync() {
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);

  useEffect(() => {
    if (!isAuthenticated) return;
    void reconcile();
  }, [isAuthenticated]);

  return null;
}

export default ConsentManagedSync;
